import type {
  MusicGenerationPrefs,
  MusicalToken,
  RhythmReadingMethod,
  RhythmicPatternPayload,
  TimeSignature,
} from '../types/music';
import { DEFAULT_MUSIC_PREFS, MUSICAL_TOKENS } from '../types/music';

export type RhythmFigure = 'whole' | 'half' | 'quarter' | 'eighth-pair' | 'sixteenth-quad' | 'rest-quarter';

const TOKEN_FIGURE: Record<MusicalToken, RhythmFigure | 'rest-whole' | 'rest-half' | 'rest-eighth' | 'rest-sixteenth'> = {
  W: 'whole',
  H: 'half',
  Q: 'quarter',
  E: 'eighth-pair',
  S: 'sixteenth-quad',
  R_W: 'rest-whole',
  R_H: 'rest-half',
  R_Q: 'rest-quarter',
  R_E: 'rest-eighth',
  R_S: 'rest-sixteenth',
};

export const TOKEN_SYMBOL: Record<MusicalToken, string> = {
  W: '𝅝',
  H: '𝅗𝅥',
  Q: '♩',
  E: '♫',
  S: '♬',
  R_W: '𝄻',
  R_H: '𝄼',
  R_Q: '𝄽',
  R_E: '𝄾',
  R_S: '𝄿',
};

/** Nombres de figuras — vocabulario chileno / MEN (no anglicismos). */
export const CHILEAN_FIGURE_LABEL: Record<MusicalToken, string> = {
  W: 'Redonda',
  H: 'Blanca',
  Q: 'Negra',
  E: 'Corchea',
  S: 'Semicorchea',
  R_W: 'Silencio de redonda',
  R_H: 'Silencio de blanca',
  R_Q: 'Silencio de negra',
  R_E: 'Silencio de corchea',
  R_S: 'Silencio de semicorchea',
};

export const FIGURE_BEATS_4_4: Record<Exclude<RhythmFigure, 'rest-quarter'>, number> = {
  whole: 4,
  half: 2,
  quarter: 1,
  'eighth-pair': 0.5,
  'sixteenth-quad': 0.25,
};

const TRADITIONAL_LABEL: Record<MusicalToken, string> = CHILEAN_FIGURE_LABEL;

const TAKADIMI_LABEL: Record<MusicalToken, string> = {
  W: 'Ta-a-a-a',
  H: 'Ta-a',
  Q: 'Ta',
  E: 'Ta-di',
  S: 'Ta-ka-di-mi',
  R_W: 'Silencio',
  R_H: 'Silencio',
  R_Q: 'Silencio',
  R_E: 'Silencio',
  R_S: 'Silencio',
};

const KODALY_LABEL: Record<MusicalToken, string> = {
  W: 'ta-a-a-a',
  H: 'ta-a',
  Q: 'Ta',
  E: 'ti-ti',
  S: 'ti-ki-ti-ki',
  R_W: 'silencio',
  R_H: 'silencio',
  R_Q: 'silencio',
  R_E: 'silencio',
  R_S: 'silencio',
};

const LEGACY_WORD_TO_TOKEN: Record<string, MusicalToken> = {
  redonda: 'W',
  blanca: 'H',
  negra: 'Q',
  corchea: 'E',
  'corchea doble': 'E',
  corchea_doble: 'E',
  'doble corchea': 'E',
  'dos corcheas': 'E',
  semicorchea: 'S',
  cuartina: 'S',
  fusa: 'S',
  silencio: 'R_Q',
  'silencio de negra': 'R_Q',
  silencio_negra: 'R_Q',
  'silencio de blanca': 'R_H',
  'silencio de redonda': 'R_W',
  'silencio de corchea': 'R_E',
  'silencio de semicorchea': 'R_S',
  'ta-a-a-a': 'W',
  'ta-a': 'H',
  ta: 'Q',
  'ta-di': 'E',
  'ta-ka-di-mi': 'S',
  'ti-ti': 'E',
  'ti-ki-ti-ki': 'S',
};

export function isMusicalToken(value: unknown): value is MusicalToken {
  return typeof value === 'string' && MUSICAL_TOKENS.includes(value as MusicalToken);
}

export function normalizeTokenArray(raw: unknown): MusicalToken[] {
  if (!Array.isArray(raw)) return [];
  const out: MusicalToken[] = [];
  for (const item of raw) {
    if (isMusicalToken(item)) {
      out.push(item);
      continue;
    }
    const text = String(item || '').trim();
    if (isMusicalToken(text)) {
      out.push(text);
      continue;
    }
    const upper = text.toUpperCase();
    if (isMusicalToken(upper)) {
      out.push(upper);
      continue;
    }
    const mapped = LEGACY_WORD_TO_TOKEN[text.toLowerCase().replace(/[()]/g, '')];
    if (mapped) out.push(mapped);
  }
  return out;
}

export function tokenToSymbol(token: MusicalToken): string {
  return TOKEN_SYMBOL[token];
}

export function tokenToFigure(token: MusicalToken): RhythmFigure {
  const fig = TOKEN_FIGURE[token];
  if (fig === 'rest-whole' || fig === 'rest-half' || fig === 'rest-eighth' || fig === 'rest-sixteenth') {
    return 'rest-quarter';
  }
  return fig as RhythmFigure;
}

export function tokenToLabel(token: MusicalToken, method: RhythmReadingMethod = 'tradicional'): string {
  switch (method) {
    case 'takadimi':
      return TAKADIMI_LABEL[token];
    case 'kodaly':
      return KODALY_LABEL[token];
    default:
      return TRADITIONAL_LABEL[token];
  }
}

export function tokenBeats(token: MusicalToken): number {
  if (token === 'R_W') return 4;
  if (token === 'R_H') return 2;
  if (token === 'R_Q') return 1;
  if (token === 'R_E') return 0.5;
  if (token === 'R_S') return 0.25;
  return FIGURE_BEATS_4_4[TOKEN_FIGURE[token] as keyof typeof FIGURE_BEATS_4_4] ?? 1;
}

export function tokensToSymbolStrip(pattern: MusicalToken[]): string {
  return pattern.map(tokenToSymbol).join(' ');
}

/** Intenta leer una secuencia de tokens separados por espacios, + o |. */
export function tryParseTokenSequence(text: string): MusicalToken[] | null {
  const raw = String(text || '').trim();
  if (!raw) return null;
  const tokens = normalizeTokenArray(raw.split(/[\s+|]+/));
  if (!tokens.length) return null;
  const withoutTokens = raw.replace(/[\s|WHEQSR_/gi]/g, '');
  if (withoutTokens.length > 3 && !/^[,.:;()¿?¡!áéíóúñ-]+$/i.test(withoutTokens)) {
    return null;
  }
  return tokens;
}

/** Quita códigos internos (Q, W/H/E…) y anglicismos del texto visible al alumno. */
export function humanizeMusicText(
  text: string,
  prefs: MusicGenerationPrefs = DEFAULT_MUSIC_PREFS,
): string {
  let t = String(text || '').trim();
  if (!t) return t;

  t = t.replace(/\(\s*(W|H|Q|E|S|R_W|R_H|R_Q|R_E|R_S)\s*\)/gi, (_, tok) =>
    tokenToLabel(String(tok).toUpperCase() as MusicalToken, 'tradicional'),
  );
  t = t.replace(/\b(W|H|Q|E|S|R_W|R_H|R_Q|R_E|R_S)\b/g, (_, tok) =>
    tokenToLabel(tok as MusicalToken, prefs.readingMethod),
  );
  t = t.replace(/\bwhole\b/gi, 'redonda');
  t = t.replace(/\bhalf\b/gi, 'blanca');
  t = t.replace(/\bquarter\b/gi, 'negra');
  t = t.replace(/\beighth(s)?\b/gi, 'corchea');
  t = t.replace(/\bsixteenth(s)?\b/gi, 'semicorchea');
  t = t.replace(/tokens?\s+W\/H\/Q\/E\/S\/R_Q/gi, 'nombres de figuras rítmicas');
  t = t.replace(/▰+/g, '♬');
  if (prefs.readingMethod !== 'takadimi') {
    t = t.replace(/\b(método\s+)?takadimi\b/gi, 'método de lectura elegido');
    t = t.replace(/\bTa-ka-di-mi\b/g, 'semicorchea');
    t = t.replace(/\bTa-di\b/g, 'corchea');
  }
  if (prefs.readingMethod !== 'kodaly') {
    t = t.replace(/\bti-ki-ti-ki\b/gi, 'semicorchea');
    t = t.replace(/\bti-ti\b/gi, 'corchea');
  }
  return t.replace(/\s{2,}/g, ' ').trim();
}

/** Formatea una alternativa de cuestionario: símbolos ♩ o nombres chilenos, nunca códigos W/Q. */
export function formatMusicOption(
  opt: string,
  prefs: MusicGenerationPrefs = DEFAULT_MUSIC_PREFS,
): string {
  const sequence = tryParseTokenSequence(opt);
  if (sequence?.length) {
    if (prefs.graphicMode === 'symbols') {
      return tokensToSymbolStrip(sequence);
    }
    return sequence.map((t) => tokenToLabel(t, prefs.readingMethod)).join(' · ');
  }
  const trimmed = String(opt || '').trim();
  if (isMusicalToken(trimmed)) {
    const tok = trimmed as MusicalToken;
    return prefs.graphicMode === 'symbols'
      ? tokenToSymbol(tok)
      : tokenToLabel(tok, prefs.readingMethod);
  }
  return humanizeMusicText(trimmed, prefs);
}

export function buildMusicTheoryRails(prefs: MusicGenerationPrefs = DEFAULT_MUSIC_PREFS): string {
  const methodLabel =
    prefs.readingMethod === 'takadimi'
      ? 'Takadimi'
      : prefs.readingMethod === 'kodaly'
        ? 'Kodály'
        : 'Tradicional chileno (Redonda, Blanca, Negra, Corchea, Semicorchea)';

  return `
[RIGOR TEÓRICO MUSICAL — CHILE / MEN]
Método de lectura del docente: ${methodLabel}
Soporte gráfico: ${prefs.graphicMode === 'symbols' ? 'Símbolos puros (Unicode)' : 'Símbolos + etiqueta breve'}

Vocabulario OBLIGATORIO en español chileno — PROHIBIDO anglicismos (whole, half, quarter, eighth).
Figuras: Redonda (W)=4, Blanca (H)=2, Negra (Q)=1, Corchea (E)=0.5, Semicorchea (S)=0.25.
Silencios: R_W=4, R_H=2, R_Q=1, R_E=0.5, R_S=0.25.
La Blanca vale 2 tiempos — NUNCA "medio tiempo".

CONTRATO INTERNO (solo JSON — el alumno NUNCA ve W/H/Q/E):
Usa arrays patternTokens con: W | H | Q | E | S | R_W | R_H | R_Q | R_E | R_S
En enunciados y alternativas escribe SOLO en español: "negra", "blanca", "corchea", "semicorchea".
PROHIBIDO: "(negra)", "(Q)", "W W H Q", "tokens W/H/Q", texto vacío en secuencias.

Ejemplos:
- completar: { "rhythmPattern": { "timeSignature": "4/4", "pattern": ["Q","E","Q","R_Q"], "missingIndex": 2 }, "text": "Completa la figura faltante en la secuencia." }
- arena: { "prompt": "¿Qué patrón tiene la misma duración total que dos negras y una blanca?", "options": ["H H", "Q Q Q Q", "W", "E E E E"], "patternTokens": null }
- order: { "patternTokens": ["Q","E","Q","H"], "printLayout": "rhythm-bar", "meter": "4/4" }
- dictado: { "patternTokens": ["E","Q","E","Q"], "teacherScript": "..." }
- anatomia: { "items": [{ "token": "Q", "correctBeats": 1 }, { "token": "H", "correctBeats": 2 }] }

MÓDULOS NATIVOS (prioridad para música — preferir sobre genéricos):
- rhythm_canvas: ejercicios rítmicos visuales (patternTokens + taskKind: complete|order|identify|match|practice)
- melody_staff: pentagrama con notas (pitch + duration)
- ear_training: dictado auditivo (teacherScript + noteNames o patternTokens)
- anatomia: duración de figuras (correctBeats — Blanca=2, NUNCA medio tiempo)

MODOS LEGACY ACEPTADOS (se convierten automáticamente a nativos con VexFlow):
- completar → rhythm_canvas complete · vincula → rhythm_canvas match · order/secuencia → rhythm_canvas order
- dictado → ear_training · arena rítmico → rhythm_canvas identify

También válidos: agrupa (clasificar figuras), desarrollo (síntesis escrita).

En preguntas de texto usa vocabulario ${methodLabel}; el renderizador dibuja los símbolos ♩ ♫ 𝅗𝅥 automáticamente.
`.trim();
}

/** Modo sin partitura: libertad creativa con rieles teóricos. */
export function buildFreeGenerativeMusicPrompt(
  prefs: MusicGenerationPrefs = DEFAULT_MUSIC_PREFS,
  focusKey = 'practical',
): string {
  return [
    '[MODO GENERATIVO PURO — SIN PARTITURA]',
    'NO hay repertorio cargado. Inventa ejercicios desde cero según el tema del docente.',
    'Libertad creativa total: decide qué notas, ritmos, melodías y secuencias usar.',
    'La única restricción son los rieles teóricos musicales (duraciones correctas, vocabulario chileno, tokens).',
    '',
    buildMusicTheoryRails(prefs),
    '',
    `[ENFOQUE SELECCIONADO]: ${focusKey}`,
    'Adapta tipos de bloque y profundidad al enfoque — puedes mezclar módulos nativos y legacy.',
  ].join('\n');
}

export function buildMusicLlmRules(prefs: MusicGenerationPrefs = DEFAULT_MUSIC_PREFS): string {
  return buildFreeGenerativeMusicPrompt(prefs);
}

export function normalizeRhythmPattern(raw: unknown): RhythmicPatternPayload | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const data = raw as Partial<RhythmicPatternPayload>;
  const pattern = normalizeTokenArray(data.pattern);
  if (pattern.length < 1) return undefined;
  const timeSignature = (['4/4', '3/4', '2/4'] as TimeSignature[]).includes(
    data.timeSignature as TimeSignature,
  )
    ? (data.timeSignature as TimeSignature)
    : '4/4';
  const missingIndex =
    typeof data.missingIndex === 'number' && data.missingIndex >= 0 && data.missingIndex < pattern.length
      ? data.missingIndex
      : undefined;
  return { timeSignature, pattern, missingIndex };
}

export interface RenderedRhythmCell {
  token: MusicalToken;
  symbol: string;
  label?: string;
  isMissing?: boolean;
}

export function renderPatternCells(
  pattern: MusicalToken[],
  prefs: MusicGenerationPrefs = DEFAULT_MUSIC_PREFS,
  missingIndex?: number,
): RenderedRhythmCell[] {
  return pattern.map((token, index) => ({
    token,
    symbol: tokenToSymbol(token),
    label:
      prefs.graphicMode === 'symbols-labels'
        ? tokenToLabel(token, prefs.readingMethod)
        : undefined,
    isMissing: missingIndex === index,
  }));
}

export function patternToTeacherScript(
  pattern: MusicalToken[],
  prefs: MusicGenerationPrefs = DEFAULT_MUSIC_PREFS,
): string {
  const symbols = pattern.map(tokenToSymbol).join(' ');
  if (prefs.readingMethod === 'tradicional') {
    return `Percuta rítmicamente el siguiente patrón para la sala: | ${symbols} |.`;
  }
  const labels = pattern.map((t) => tokenToLabel(t, prefs.readingMethod)).join(' ');
  return `Percuta rítmicamente el siguiente patrón para la sala: | ${symbols} | (${labels}).`;
}

export function tokensToOrderItems(
  pattern: MusicalToken[],
  prefs: MusicGenerationPrefs = DEFAULT_MUSIC_PREFS,
): string[] {
  if (prefs.readingMethod === 'tradicional' && prefs.graphicMode === 'symbols') {
    return pattern.map(tokenToSymbol);
  }
  return pattern.map((t) => tokenToLabel(t, prefs.readingMethod));
}
